import { z } from 'zod';
import { paginationQuerySchema, paginated } from './common.js';
export const policySortSchema = z.enum(['name', 'eventType', 'createdAt']);
export const policyListQuerySchema = paginationQuerySchema
    .extend({
    sort: policySortSchema.default('name'),
    direction: z.enum(['asc', 'desc']).default('asc'),
    eventType: z.string().optional(),
    status: z.string().optional(),
    search: z.string().trim().min(1).optional(),
    /*
     * `z.enum` rather than `z.coerce.boolean()` — risk R8. Coercion makes the
     * STRING "false" truthy, so `?includeArchived=false` would include them.
     */
    includeArchived: z.enum(['true', 'false']).optional(),
})
    .strict();
/** Reversible states a policy can be moved between. */
export const policyLifecycleActionSchema = z.enum([
    'deactivate',
    'reactivate',
    'archive',
    'restore',
]);
/*
 * The action travels in the BODY, not in the path.
 *
 * `POST :id/(deactivate|archive|…)` reads better, but it puts the set of legal
 * actions in an Express path pattern where nothing validates it and the OpenAPI
 * document renders it as a literal regex. In the body it is one zod enum, shared
 * by the client, and a typo is a 400 that names the four options.
 */
export const policyLifecycleRequestSchema = z
    .object({
    action: policyLifecycleActionSchema,
    note: z.string().trim().max(500).optional(),
})
    .strict();
export const policyListItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    eventType: z.string(),
    status: z.string(),
    versionCount: z.number().int(),
    activeVersion: z.number().int().nullable(),
    createdAt: z.string(),
    /** Soft-deleted. Hidden from the default list, restorable, never evaluated. */
    archived: z.boolean(),
    /**
     * How many evaluations this policy has recorded.
     *
     * Carried because it is what makes deletion refusable in words rather than by
     * a bare 409 — "this policy has shaped 16 evaluations" is a reason; "cannot
     * delete" is not.
     */
    evaluationCount: z.number().int(),
    /**
     * True only when the policy has never influenced anything: no evaluations and
     * no recommendations. "Nothing is hard-deleted" protects HISTORY, and a
     * policy that never ran is not history — it is a typo. Everything else
     * archives.
     */
    deletable: z.boolean(),
});
/**
 * The values this tenant's policies actually carry, for the filters.
 *
 * ⚠️ Not the canonical enum. `eventType` is a STRING on read deliberately —
 * rows created before the write guard carry values outside `POLICY_EVENT_TYPES`
 * and must still render — and `status` has no enum at all. A dropdown built
 * from a list of what is *possible* would offer options that match nothing and
 * omit values that are right there on the page.
 */
export const policyFacetsSchema = z.object({
    eventTypes: z.array(z.string()),
    statuses: z.array(z.string()),
});
export const policyListResponseSchema = paginated(policyListItemSchema).extend({
    facets: policyFacetsSchema,
});
export const policyVersionSchema = z.object({
    id: z.string().uuid(),
    policyId: z.string().uuid(),
    version: z.number().int(),
    definition: z.record(z.unknown()),
    isActive: z.boolean(),
    createdAt: z.string(),
    /**
     * When this version FIRST went live, or null if it never has.
     *
     * `isActive` is the present tense; this is the past. Without it a superseded
     * version and an abandoned draft are the same row — both `isActive: false` —
     * and only one of them is safe to discard.
     */
    activatedAt: z.string().nullable(),
    /** How many recorded evaluations name this version. */
    evaluationCount: z.number().int(),
    /**
     * True only for a version that has never governed anything: never activated,
     * not active now, and named by no evaluation, recommendation or outcome. That
     * is an abandoned draft, not history — see `policyListItemSchema.deletable`
     * for the same rule one level up.
     */
    discardable: z.boolean(),
});
export const policyDetailSchema = policyListItemSchema.extend({
    versions: z.array(policyVersionSchema),
});
export const policyVersionListResponseSchema = z.object({
    policyId: z.string().uuid(),
    versions: z.array(policyVersionSchema),
});
// ── writes ──────────────────────────────────────────────────────────────────
/**
 * The canonical event types a policy may be written against.
 *
 * Mirrors `EVENT_TYPES` in @oip/domain, which is the authority — the two
 * packages deliberately do not depend on each other, so this is kept in step by
 * `policy-event-types.test.ts` rather than by an import, the same way the
 * alerting vocabularies are.
 *
 * It is an enum and not a free string because a policy names the event type it
 * governs, and the engine selects policies by matching that string exactly. A
 * typo is therefore not a validation error at any point: the policy is stored,
 * can be activated, and simply never fires. `TestPolicy` on `newEvent` was
 * already sitting in a database when this was tightened.
 */
export const POLICY_EVENT_TYPES = [
    'OVER_SPEED',
    'GEOFENCE_EXIT',
    'BATTERY_POWER_ZERO',
    'HARSH_BRAKING',
    'HARSH_ACCELERATION',
    'SHARP_TURN',
    'FUEL_DROP',
    'FUEL_INCREASE',
    'DRIVER_SEATBELT_UNFASTENED',
    'PASSENGER_SEATBELT_UNFASTENED',
    // Added 10 September 2026 with the PTO and AdBlue parameters. Mirrors
    // `EVENT_TYPES` in @oip/domain — the packages do not depend on each other and
    // `policy-event-types.test.ts` is what keeps the two copies in step, so a
    // canonical type added later can never be one policies are unable to target.
    'ADBLUE_REFILL_REQUIRED',
    'PTO_ENGAGED_WHILE_MOVING',
    // Drivers' hours. Four types for eight alerts — "approaching" and "reached"
    // are one condition at two severities, and two types would be two incidents
    // about one break. See `EVENT_TYPES` in @oip/domain for the full argument.
    'DRIVERS_HOURS_BREAK_DUE',
    'DRIVERS_HOURS_REST_DUE',
    'DRIVERS_HOURS_DAILY_LIMIT',
    'DRIVERS_HOURS_WEEKLY_LIMIT',
    // Driver card. Every one carries the privacy-mode ambiguity: the provider
    // collapses the device's four-valued state to a boolean upstream, so "no
    // card" cannot be told from a legitimate out-of-scope journey.
    'DRIVER_CARD_MISSING',
    'DRIVER_CARD_CHANGED',
    'DRIVER_CARD_NOT_RECOGNISED',
    'ALARM_WHILE_STATIONARY',
    'ALARM_WHILE_MOVING',
    'TOW_ALERT',
    'IMMOBILISER_ENGAGED',
    'IMMOBILISER_DISENGAGED',
    'FUEL_CARD_FRAUD_SUSPECTED',
    'BASELINE_DEVIATION',
    'MAINTENANCE_DUE',
    /*
     * ⚠️ Listed even though none of them raises an incident, and that is the
     * point of this mirror: it is `EVENT_TYPES` seen from the other package, and
     * `policy-event-types.test.ts` asserts the two match EXACTLY. Omitting the
     * four would make a canonical type one policies are unable to target — which
     * is the fault this mirror was created to prevent, arriving by tidiness.
     *
     * A tenant CAN write a policy against them; the stock set does not, because a
     * movement event is not a finding. See the note in `EVENT_TYPES`.
     */
    'DEVICE_MOVING',
    'DEVICE_STOPPED',
    'DEVICE_OFFLINE',
    'DEVICE_ONLINE',
];
export const policyEventTypeSchema = z.enum(POLICY_EVENT_TYPES);
export const createPolicyRequestSchema = z.object({
    name: z.string().min(1).max(120),
    /*
     * Constrained on WRITE only. Reads stay a plain string, because rows created
     * before this existed carry values outside the list and must still render —
     * hiding them would make an inert policy invisible instead of visible and
     * wrong.
     */
    eventType: policyEventTypeSchema,
});
/**
 * Policy definition, validated STRUCTURALLY at the API boundary.
 *
 * This previously accepted `z.record(z.unknown())` — any JSON object at all,
 * including `{}`. A malformed definition could therefore be created AND
 * activated, and only failed later when an event was evaluated against it, by
 * which point it was live and shaping incidents. For a governance action guarded
 * by a confirmation dialog that was the wrong place to find out.
 *
 * Mirrors `PolicyDefinition` in @oip/domain. Restated rather than imported
 * because @oip/domain holds the interfaces and this package holds the wire
 * contract; the evaluator remains the authority on SEMANTICS (does this field
 * path exist, is this score sensible) — this only rejects shapes it could never
 * interpret.
 */
export const policyFieldOperatorSchema = z.enum([
    'EQ',
    'NEQ',
    'GT',
    'GTE',
    'LT',
    'LTE',
    'IN',
    'EXISTS',
]);
export const policyFieldConditionSchema = z.object({
    field: z.string().min(1),
    operator: policyFieldOperatorSchema,
    // Absent for EXISTS; unknown rather than any so callers must narrow it.
    value: z.unknown().optional(),
});
export const policyConditionSchema = z.lazy(() => z.union([
    policyFieldConditionSchema,
    z.object({ all: z.array(policyConditionSchema).min(1) }),
    z.object({ any: z.array(policyConditionSchema).min(1) }),
    z.object({ not: policyConditionSchema }),
]));
export const policyBaseScoreRuleSchema = z.object({
    condition: policyConditionSchema,
    score: z.number(),
    reason: z.string().min(1),
});
export const policyRiskModifierSchema = z.object({
    name: z.string().min(1),
    condition: policyConditionSchema,
    scoreImpact: z.number(),
    reason: z.string().min(1),
});
export const policyActionRuleSchema = z.object({
    // Omitted means "default action" — the last rule usually has no condition.
    condition: policyConditionSchema.optional(),
    actionCategory: z.string().min(1),
    recommendedActionTemplate: z.string().min(1),
    explanationTemplate: z.string().min(1),
    approvalRequired: z.boolean(),
});
export const policyDefinitionSchema = z.object({
    condition: policyConditionSchema,
    result: z.object({
        priority: z.number().int(),
        baseScoreRules: z.array(policyBaseScoreRuleSchema),
        riskModifiers: z.array(policyRiskModifierSchema),
        actionRules: z.array(policyActionRuleSchema),
    }),
});
/**
 * Replace a DRAFT version's definition in place.
 *
 * Versions are immutable, and that invariant protects HISTORY: every evaluation
 * and recommendation points at the exact version that decided it. A version
 * never activated and named by nothing has no history to protect — it is the
 * same set the server already lets you DELETE, and editing it destroys strictly
 * less than discarding it does.
 *
 * The version NUMBER and id are preserved deliberately: iterating on a rule
 * should refine v3, not accumulate v3, v4, v5.
 */
export const updatePolicyVersionRequestSchema = z
    .object({ definition: policyDefinitionSchema })
    .strict();
export const createPolicyVersionRequestSchema = z.object({
    definition: policyDefinitionSchema,
    /** Optional explicit version number; server assigns next if omitted. */
    version: z.number().int().positive().optional(),
});
// ── policy backtest ─────────────────────────────────────────────────────────
/**
 * What a draft version would have done to evaluations that already happened.
 *
 * `UNDECIDABLE` is a first-class result, never folded into "would not fire": a
 * draft may read a field the recording never captured, and calling that a
 * non-match would overstate every improvement. See
 * `docs/architecture/policy-backtest-design.md`.
 */
export const backtestVerdictSchema = z.enum([
    'IMPROVED',
    'REGRESSED',
    'SILENCED',
    'UNCHANGED',
    'NEW',
    'UNDECIDABLE',
]);
export const policyBacktestRequestSchema = z
    .object({
    /** Cost is linear in stored evaluations, so the window is bounded. */
    days: z.coerce.number().int().min(1).max(180).default(30),
})
    .strict();
export const policyBacktestItemSchema = z.object({
    incidentId: z.string().uuid(),
    canonicalEventId: z.string().uuid(),
    verdict: backtestVerdictSchema,
    /** Null for UNDECIDABLE — unknown, not false. */
    wouldMatch: z.boolean().nullable(),
    missingFields: z.array(z.string()),
});
export const policyBacktestResponseSchema = z.object({
    policyId: z.string().uuid(),
    policyVersionId: z.string().uuid(),
    windowDays: z.number().int(),
    evaluated: z.number().int(),
    /** Every verdict is present, so the client cannot render a bucket the server did not send. */
    counts: z.object({
        IMPROVED: z.number().int(),
        REGRESSED: z.number().int(),
        SILENCED: z.number().int(),
        UNCHANGED: z.number().int(),
        NEW: z.number().int(),
        UNDECIDABLE: z.number().int(),
    }),
    /** Deduplicated fields that caused an UNDECIDABLE — what to fix. */
    undecidableFields: z.array(z.string()),
    /** Bounded sample so the numbers are checkable rather than trusted. */
    samples: z.array(policyBacktestItemSchema),
});
//# sourceMappingURL=policy.js.map